/**
 * Copy for the web landing page (/landing). Wording follows
 * docs/LEGAL_PRIVACY.md §4: awareness and habit — never diagnose, treat,
 * or promise relief.
 */

/**
 * Store listings, null until each store has a public listing. While null the
 * badge shows "Coming soon" and the page points at the web app instead.
 * Without a country code each store sends people to their own region.
 *   App Store:  https://apps.apple.com/app/id<Apple ID from App Store Connect>
 *   Google Play: https://play.google.com/store/apps/details?id=com.uihyun.posture
 */
export const APP_STORE_URL: string | null = null;
export const PLAY_STORE_URL: string | null = null;

/**
 * The address to share and to show: the app is at the root, this page at
 * /landing. landing/index.html repeats it in its link-preview tags.
 */
export const SITE_URL = 'https://erectus.app';

export function isLandingPath(pathname: string): boolean {
  return pathname.startsWith('/landing');
}

export const HERO = {
  title: 'Erectus',
  lead: 'Stand your phone at your side while you work.\nSee how your sitting posture holds up over the hour.',
};

export const STEPS = [
  {
    title: 'Stand your phone at your side',
    body: 'Chest height, about two metres (six feet) away. A live checklist tells you when the view works.',
  },
  {
    title: 'Work as usual',
    body: 'Neck, trunk and shoulder angles are measured a few times a second, on the phone itself.',
  },
  {
    title: 'See where it slipped',
    body: 'A score, a minute-by-minute timeline, and the moment your posture first started to drift.',
  },
] as const;

/** Carousel: public/landing/<file>.webp, made by scripts/store-shots/compose.tsx. */
export const SHOTS = [
  { file: 'session-upright', caption: 'Live, from the side' },
  { file: 'session-slouch', caption: 'Spots the slouch' },
  { file: 'report', caption: 'A report every session' },
  { file: 'report-timeline', caption: 'When it wore down' },
  { file: 'progress-weekly', caption: 'Week by week' },
  { file: 'coach', caption: 'Ask Darwin, your coach' },
] as const;

export const FEATURES = [
  { title: 'Five stages to evolve through', body: 'Your character straightens up on a 4-week average, so one rough day never sets you back.' },
  { title: 'Darwin, your posture coach', body: 'Ask about your own sessions — ten questions a day.' },
  { title: 'Desk-friendly exercises', body: 'Short, general moves commonly used for posture and mobility.' },
  { title: 'Hands-free start', body: 'Tap once, sit down, and the session starts when you are in view.' },
] as const;

export const PRIVACY_POINTS = [
  'Video never leaves your phone. Frames are analyzed in memory and discarded.',
  'Only numbers are stored: angles, scores and session timings.',
  'Delete your account and everything goes with it.',
  'Sharing data to improve detection is a separate, optional choice for adults.',
] as const;

export const FAQ = [
  {
    q: 'Is this a medical device?',
    a: 'No. Erectus is a wellness and educational tool. It does not diagnose, treat or prevent any condition. If you have pain, talk to a physical therapist or doctor.',
  },
  {
    q: 'How accurate is it?',
    a: 'Angles are estimated from a single camera view and carry roughly ±5° of error. The app shows that margin and treats smaller week-to-week changes as unchanged.',
  },
  {
    q: 'What do I need?',
    a: 'A phone with a camera, something to stand it on, and a side-on view of where you sit. Fitted clothing and a visible ear help.',
  },
  {
    q: 'How is this different from head-tracking earbuds?',
    a: 'Earbuds see head angle only. A side camera sees your neck, trunk and shoulders together, over the whole session.',
  },
] as const;

export const CTA = {
  title: 'Sit taller,\none week at a time.',
  body: 'All it takes is your phone, standing beside your desk.',
};

export const LEGAL_LINKS = [
  { title: 'Privacy', href: '/legal/privacy-policy' },
  { title: 'Consumer Health Data', href: '/legal/consumer-health-data' },
  { title: 'Terms', href: '/legal/terms' },
  { title: 'Medical Disclaimer', href: '/legal/medical-disclaimer' },
] as const;
